import axios, { AxiosResponse } from "axios";

import { createParams } from "@winshop-crm/lib";
import {
  EventsFilterRequest,
  EventsFilterResponse,
  Serialized,
  TaskDetail,
  TasksFilterRequest,
  TasksFilterResponse,
} from "@winshop-crm/types";

/** Query params as the client holds them — blanks/nulls are dropped by `createParams`. */
type QueryFor<T> = Partial<Record<keyof T, string | null>>;

const API_URL = `${process.env.NEXT_PUBLIC_URL}/api`;

/**
 * `_axiosGet` swallows request errors and resolves with the `AxiosError`, whose
 * `data` is `undefined` — hence every result is `T | undefined`.
 */
const sharedFetch = async <T>(path: string): Promise<T | undefined> => {
  const url = API_URL + path;
  return (await _axiosGet<T>(url)).data;
};

export const api = {
  sharedFetch,

  events: async (request: QueryFor<EventsFilterRequest> & { userId: string; token: string }) =>
    await sharedFetch<Serialized<EventsFilterResponse>[]>(
      `/events${createParams({ ...request, numberOfEvents: "1000" })}`
    ),

  event: async (eventId: string) =>
    await sharedFetch<Serialized<EventsFilterResponse>[]>(`/events/${eventId}`),

  tasks: async (request: QueryFor<TasksFilterRequest> & { userId: string; token: string }) =>
    await sharedFetch<Serialized<TasksFilterResponse>[]>(
      `/tasks${createParams({ ...request, numberOfTasks: "1000" })}`
    ),

  task: async ({ userId, token, taskId }: { userId: string; token: string; taskId: string }) =>
    await sharedFetch<TaskDetail>(
      `/tasks/${taskId}${createParams({ userId: userId, token: token })}`
    ),
};

const _axiosGet = <T>(url: string): Promise<AxiosResponse<T>> =>
  axios
    .get<T>(url, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(async (res) => {
      if (res.status >= 400) throw new Error(JSON.stringify(res.data) ?? res.statusText);
      return res;
    })
    .catch((err) => {
      // oxlint-disable-next-line eslint/no-console
      console.warn(err);
      return err;
    });
